import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Card,
  Button,
  Table,
  Badge,
  Modal,
  Form,
  Row,
  Col,
  Tab,
  Nav,
  Spinner,
} from "react-bootstrap";
import {
  FaUserShield,
  FaPlus,
  FaEdit,
  FaTrash,
  FaCheck,
  FaUsersCog,
  FaLock,
  FaCheckSquare,
  FaSquare,
} from "react-icons/fa";
import { toast } from "react-toastify";
import roleService from "../../../services/roleService";
import HasPermission from "../../Common/HasPermission";
import {
  hasPermission,
  isSuperAdmin,
  PERMISSIONS,
} from "../../../utils/permissionManager";

const RolesList = () => {
  const { user } = useSelector((state) => state.auth);

  // Permission evaluation
  const canManageUserRoles =
    isSuperAdmin(user) ||
    hasPermission(user, [
      PERMISSIONS.USERS_MANAGE_ROLES,
      PERMISSIONS.USERS_MANAGE,
    ]);

  const canManageRoles =
    isSuperAdmin(user) ||
    hasPermission(user, [PERMISSIONS.ROLES_MANAGE]);

  const [activeTab, setActiveTab] = useState(canManageRoles ? "roles" : "users");
  const [roles, setRoles] = useState([]);
  const [usersWithRoles, setUsersWithRoles] = useState([]);
  const [groupedPermissions, setGroupedPermissions] = useState({});
  const [loading, setLoading] = useState(false);

  // Role Modal state
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleForm, setRoleForm] = useState({
    role_name: "",
    description: "",
    permission_ids: [],
  });
  const [savingRole, setSavingRole] = useState(false);

  // User Assign Modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [assigningRole, setAssigningRole] = useState(false);

  useEffect(() => {
    if (!canManageUserRoles && activeTab === "users") {
      setActiveTab("roles");
    }
  }, [canManageUserRoles, activeTab]);

  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageUserRoles, canManageRoles]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const requests = [
        roleService.getRoles(),
        roleService.getAllPermissions(),
      ];

      // Only fetch users if authorized
      if (canManageUserRoles) {
        requests.push(roleService.getUsersWithRoles());
      }

      const results = await Promise.all(requests);
      const rolesRes = results[0];
      const permsRes = results[1];
      const usersRes = canManageUserRoles ? results[2] : null;

      setRoles(rolesRes?.data || []);
      setGroupedPermissions(permsRes?.data?.grouped || {});
      if (usersRes?.data) {
        setUsersWithRoles(usersRes.data);
      }
    } catch (error) {
      console.error("Error fetching RBAC data:", error);
      toast.error("Failed to load roles and permissions data");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateModal = () => {
    setIsEditing(false);
    setSelectedRole(null);
    setRoleForm({
      role_name: "",
      description: "",
      permission_ids: [],
    });
    setShowRoleModal(true);
  };

  const handleOpenEditModal = async (role) => {
    try {
      setLoading(true);
      const res = await roleService.getRoleById(role.id);
      const roleDetails = res.data;
      setSelectedRole(roleDetails);
      setIsEditing(true);
      setRoleForm({
        role_name: roleDetails.role_name,
        description: roleDetails.description || "",
        permission_ids: roleDetails.permissions?.map((p) => p.id) || [],
      });
      setShowRoleModal(true);
    } catch (error) {
      toast.error("Failed to load role details");
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePermission = (permId) => {
    setRoleForm((prev) => {
      const exists = prev.permission_ids.includes(permId);
      return {
        ...prev,
        permission_ids: exists
          ? prev.permission_ids.filter((id) => id !== permId)
          : [...prev.permission_ids, permId],
      };
    });
  };

  const handleToggleModulePermissions = (modulePerms) => {
    const modulePermIds = modulePerms.map((p) => p.id);
    const allSelected = modulePermIds.every((id) =>
      roleForm.permission_ids.includes(id)
    );

    setRoleForm((prev) => {
      let updated;
      if (allSelected) {
        // Deselect all in module
        updated = prev.permission_ids.filter((id) => !modulePermIds.includes(id));
      } else {
        // Select all in module
        const toAdd = modulePermIds.filter(
          (id) => !prev.permission_ids.includes(id)
        );
        updated = [...prev.permission_ids, ...toAdd];
      }
      return { ...prev, permission_ids: updated };
    });
  };

  const handleSaveRole = async (e) => {
    e.preventDefault();
    if (!roleForm.role_name.trim()) {
      toast.warning("Role name is required");
      return;
    }

    setSavingRole(true);
    try {
      if (isEditing) {
        await roleService.updateRole(selectedRole.id, roleForm);
        toast.success(`Role "${roleForm.role_name}" updated successfully`);
      } else {
        await roleService.createRole(roleForm);
        toast.success(`Role "${roleForm.role_name}" created successfully`);
      }
      setShowRoleModal(false);
      fetchInitialData();
    } catch (error) {
      console.error("Save role error:", error);
      toast.error(error.response?.data?.message || "Failed to save role");
    } finally {
      setSavingRole(false);
    }
  };

  const handleDeleteRole = async (role) => {
    if (role.is_system) {
      toast.warning("System roles cannot be deleted");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to delete the role "${role.role_name}"?`
      )
    ) {
      return;
    }

    try {
      await roleService.deleteRole(role.id);
      toast.success(`Role "${role.role_name}" deleted`);
      fetchInitialData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete role");
    }
  };

  const handleOpenAssignModal = (user) => {
    setSelectedUser(user);
    setSelectedRoleId(user.role_id || "");
    setShowAssignModal(true);
  };

  const handleAssignRoleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedRoleId) {
      toast.warning("Please select a role");
      return;
    }

    setAssigningRole(true);
    try {
      await roleService.assignRoleToUser(selectedUser.id, selectedRoleId);
      toast.success("Role assigned to user successfully");
      setShowAssignModal(false);
      fetchInitialData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to assign role");
    } finally {
      setAssigningRole(false);
    }
  };

  return (
    <div className="container-fluid p-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h3 className="fw-bold mb-1 d-flex align-items-center gap-2">
            <FaUserShield className="text-primary" /> Roles & Permissions
          </h3>
          <p className="text-muted mb-0">
            Define roles, configure granular permissions, and assign access levels to staff.
          </p>
        </div>

        <HasPermission permission="roles.manage">
          <Button
            variant="primary"
            className="d-flex align-items-center gap-2"
            onClick={handleOpenCreateModal}
          >
            <FaPlus /> Create Custom Role
          </Button>
        </HasPermission>
      </div>

      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
        <Nav variant="pills" className="mb-4 bg-light p-2 rounded">
          {canManageRoles && (
            <Nav.Item>
              <Nav.Link eventKey="roles" className="d-flex align-items-center gap-2">
                <FaUserShield /> Roles & Permission Matrix
              </Nav.Link>
            </Nav.Item>
          )}
          {canManageUserRoles && (
            <Nav.Item>
              <Nav.Link eventKey="users" className="d-flex align-items-center gap-2">
                <FaUsersCog /> Assign User Roles ({usersWithRoles.length})
              </Nav.Link>
            </Nav.Item>
          )}
        </Nav>

        <Tab.Content>
          {/* Roles Tab */}
          {canManageRoles && (
            <Tab.Pane eventKey="roles">
              <Card className="shadow-sm border-0">
                <Card.Body className="p-0">
                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" variant="primary" />
                      <p className="text-muted mt-2">Loading roles and permissions...</p>
                    </div>
                  ) : (
                    <Table responsive hover className="mb-0 align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>Role Name</th>
                          <th>Description</th>
                          <th>Type</th>
                          <th className="text-center">Permissions</th>
                          <th className="text-center">Active Users</th>
                          <th className="text-end">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {roles.map((role) => (
                          <tr key={role.id}>
                            <td>
                              <div className="fw-bold text-dark">{role.role_name}</div>
                              <small className="text-muted">{role.role_key}</small>
                            </td>
                            <td style={{ maxWidth: "300px" }}>
                              {role.description || <span className="text-muted">-</span>}
                            </td>
                            <td>
                              {role.is_system ? (
                                <Badge bg="dark" className="px-2 py-1">
                                  <FaLock className="me-1" size={10} /> System Default
                                </Badge>
                              ) : (
                                <Badge bg="info" className="px-2 py-1">Custom Role</Badge>
                              )}
                            </td>
                            <td className="text-center">
                              <Badge bg="primary" pill className="px-2 py-1">
                                {role.is_system && role.role_key === "super_admin"
                                  ? "All (38)"
                                  : `${role.permission_count || 0} permissions`}
                              </Badge>
                            </td>
                            <td className="text-center">
                              <Badge bg="secondary" pill className="px-2 py-1">
                                {role.user_count || 0} users
                              </Badge>
                            </td>
                            <td className="text-end">
                              <div className="d-flex justify-content-end gap-2">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleOpenEditModal(role)}
                                  title="Edit Role & Permissions"
                                >
                                  <FaEdit />
                                </Button>
                                {!role.is_system && (
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleDeleteRole(role)}
                                    title="Delete Role"
                                  >
                                    <FaTrash />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            </Tab.Pane>
          )}

          {/* Users Assignment Tab */}
          {canManageUserRoles && (
            <Tab.Pane eventKey="users">
              <Card className="shadow-sm border-0">
                <Card.Header className="bg-white border-bottom py-3">
                  <h5 className="fw-bold mb-0">Admin & Staff Users</h5>
                </Card.Header>
                <Card.Body className="p-0">
                  <Table responsive hover className="mb-0 align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>User</th>
                        <th>Email</th>
                        <th>Current Role</th>
                        <th>Account Status</th>
                        <th className="text-end">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usersWithRoles.map((u) => (
                        <tr key={u.id}>
                          <td>
                            <div className="fw-bold">
                              {u.first_name} {u.last_name}
                            </div>
                            <small className="text-muted">{u.phone || "No phone"}</small>
                          </td>
                          <td>{u.email}</td>
                          <td>
                            {u.role_name ? (
                              <Badge bg="success" className="px-2 py-1">
                                {u.role_name}
                              </Badge>
                            ) : (
                              <Badge bg="warning" text="dark" className="px-2 py-1">
                                {u.user_type === "super_admin" ? "Super Admin" : "No Role Assigned"}
                              </Badge>
                            )}
                          </td>
                          <td>
                            <Badge bg={u.is_active ? "success" : "danger"}>
                              {u.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </td>
                          <td className="text-end">
                            <Button
                              // variant="outline-primary"
                              size="sm"
                              className="d-inline-flex align-items-center gap-1"
                              onClick={() => handleOpenAssignModal(u)}
                            >
                              <FaUsersCog /> Change Role
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Tab.Pane>
          )}
        </Tab.Content>
      </Tab.Container>

      {/* CREATE / EDIT ROLE MODAL */}
      <Modal
        show={showRoleModal}
        onHide={() => setShowRoleModal(false)}
        size="lg"
        centered
      >
        <Form onSubmit={handleSaveRole}>
          <Modal.Header closeButton>
            <Modal.Title className="fw-bold">
              {isEditing ? `Edit Role: ${selectedRole?.role_name}` : "Create New Role"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: "75vh", overflowY: "auto" }}>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Role Name *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. Product Manager"
                    value={roleForm.role_name}
                    onChange={(e) =>
                      setRoleForm({ ...roleForm, role_name: e.target.value })
                    }
                    disabled={isEditing && selectedRole?.is_system}
                    required
                  />
                  {isEditing && selectedRole?.is_system && (
                    <Form.Text className="text-muted small">
                      System default role name cannot be changed
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Description</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Brief description of role responsibilities"
                    value={roleForm.description}
                    onChange={(e) =>
                      setRoleForm({ ...roleForm, description: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <h6 className="fw-bold mb-3 border-bottom pb-2">
              Configure Module Permissions
            </h6>

            {Object.keys(groupedPermissions).map((moduleKey) => {
              const modulePerms = groupedPermissions[moduleKey] || [];
              const allSelected = modulePerms.every((p) =>
                roleForm.permission_ids.includes(p.id)
              );

              return (
                <Card key={moduleKey} className="mb-3 border bg-light">
                  <Card.Header className="d-flex justify-content-between align-items-center bg-white py-2">
                    <span className="fw-bold text-uppercase text-primary" style={{ fontSize: "13px" }}>
                      {moduleKey === "returns" ? "Returns & Exchanges" : moduleKey.replace("_", " ")}
                    </span>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 text-decoration-none"
                      onClick={() => handleToggleModulePermissions(modulePerms)}
                    >
                      {allSelected ? "Deselect All" : "Select All"}
                    </Button>
                  </Card.Header>
                  <Card.Body className="py-2">
                    <Row>
                      {modulePerms.map((perm) => {
                        const isChecked = roleForm.permission_ids.includes(perm.id);
                        return (
                          <Col md={6} key={perm.id} className="mb-2">
                            <Form.Check
                              type="checkbox"
                              id={`perm-${perm.id}`}
                              label={
                                <div>
                                  <div className="fw-semibold" style={{ fontSize: "13px" }}>
                                    {perm.display_name}
                                  </div>
                                  <small className="text-muted" style={{ fontSize: "11px" }}>
                                    {perm.description || perm.permission_key}
                                  </small>
                                </div>
                              }
                              checked={isChecked}
                              onChange={() => handleTogglePermission(perm.id)}
                            />
                          </Col>
                        );
                      })}
                    </Row>
                  </Card.Body>
                </Card>
              );
            })}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowRoleModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={savingRole}>
              {savingRole ? "Saving..." : isEditing ? "Update Role" : "Create Role"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* ASSIGN ROLE MODAL */}
      <Modal
        show={showAssignModal}
        onHide={() => setShowAssignModal(false)}
        centered
      >
        <Form onSubmit={handleAssignRoleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title className="fw-bold">Assign Role to User</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedUser && (
              <div className="mb-3 p-3 bg-light rounded">
                <div className="fw-bold">{selectedUser.first_name} {selectedUser.last_name}</div>
                <div className="text-muted small">{selectedUser.email}</div>
              </div>
            )}

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Select Assigned Role *</Form.Label>
              <Form.Select
                value={selectedRoleId}
                onChange={(e) => setSelectedRoleId(e.target.value)}
                required
              >
                <option value="">-- Choose a Role --</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.role_name} {r.is_system ? "(System Default)" : ""}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAssignModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={assigningRole}>
              {assigningRole ? "Assigning..." : "Assign Role"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default RolesList;
